/* Fiche produit ZenSpire (mission 490271) : sélecteur de formule, barre d'achat fixe, vidéos de professionnels.
   Sans dépendance : le thème garde son propre formulaire d'achat, on lui règle seulement la quantité.
   Le script est chargé en module asynchrone : un élément peut être « connecté » avant que ses enfants soient lus
   par le navigateur, donc chaque élément attend la fin de la lecture du document avant de chercher ses enfants. */

const sansAnimation = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const petitEcran = () => window.matchMedia('(max-width: 749px)').matches;

/** Appelle `demarrer` tout de suite si le document est lu, sinon à la fin de sa lecture */
const quandPret = (demarrer) => {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrer, { once: true });
  else demarrer();
};

/* ---------- Formules : règle la quantité du formulaire d'achat et affiche le total sur le bouton ---------- */
/** La formule de l'URL ne se pose qu'une fois : si le thème redessine la section, le choix du visiteur n'est pas écrasé */
let lienApplique = false;

class ZsFormules extends HTMLElement {
  #observeur;

  connectedCallback() {
    this.addEventListener('change', this.#maj);
    window.addEventListener('pageshow', this.#maj);
    quandPret(() => {
      this.#depuisLeLien();
      this.#maj();
      // Le thème peut redessiner le bouton d'achat (changement de variante) : on y remet le total
      const bouton = this.#section?.querySelector('product-form-component add-to-cart-component');
      if (bouton && !this.#observeur) {
        this.#observeur = new MutationObserver(() => this.#total());
        this.#observeur.observe(bouton, { childList: true, subtree: true });
      }
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#maj);
    window.removeEventListener('pageshow', this.#maj);
    this.#observeur?.disconnect();
    this.#observeur = undefined;
  }

  get #section() {
    return this.closest('.shopify-section');
  }

  /** @returns {{quantite: number, total: string, court: string} | null} */
  get etat() {
    const r = this.querySelector('input[type="radio"]:checked');
    return r ? { quantite: Number(r.dataset.quantite) || 1, total: r.dataset.total || '', court: r.dataset.court || '' } : null;
  }

  /** Arrivée par un lien « …/produit#zs-formule-2 » (le bouton « Choisir le Duo » de l'accueil) : la formule est cochée, sans défilement (le navigateur a déjà rejoint l'ancre) */
  #depuisLeLien() {
    if (lienApplique) return;
    lienApplique = true;
    const arrivee = /^#zs-formule-(\d+)$/.exec(window.location.hash);
    if (!arrivee) return;
    const radio = [...this.querySelectorAll('input[type="radio"]')].find((r) => Number(r.dataset.quantite) === Number(arrivee[1]));
    if (radio) radio.checked = true;
  }

  /** Coche la formule de cette quantité, la montre et rend la main au bouton d'achat */
  choisir(quantite) {
    const radio = [...this.querySelectorAll('input[type="radio"]')].find((r) => Number(r.dataset.quantite) === quantite);
    if (!radio) return false;
    radio.checked = true;
    this.#maj();
    this.scrollIntoView({ behavior: sansAnimation() ? 'auto' : 'smooth', block: 'center' });
    radio.focus({ preventScroll: true });
    return true;
  }

  #maj = () => {
    const e = this.etat;
    if (!e) return;
    const section = this.#section;
    const champ = section?.querySelector('product-form-component input[name="quantity"]');
    if (champ) champ.value = String(e.quantite);
    section?.querySelector('quantity-selector-component')?.setValue?.(e.quantite);
    this.#total();
    document.dispatchEvent(new CustomEvent('zs:formule', { detail: e }));
  };

  #total() {
    const e = this.etat;
    const contenu = this.#section?.querySelector('product-form-component .add-to-cart-text__content');
    if (!e || !contenu) return;
    let t = contenu.querySelector('.zs-total-bouton');
    if (!t) {
      t = document.createElement('span');
      t.className = 'zs-total-bouton';
      contenu.append(t);
    }
    const texte = ` · ${e.total}`;
    if (t.textContent !== texte) t.textContent = texte;
  }
}
if (!customElements.get('zs-formules')) customElements.define('zs-formules', ZsFormules);

/* Lien « Choisir le Duo » : #zs-formule-2 sélectionne la formule de 2 appareils et remonte au bouton d'achat */
document.addEventListener('click', (ev) => {
  const lien = ev.target instanceof Element ? ev.target.closest('a[href^="#zs-formule-"]') : null;
  if (!lien) return;
  const quantite = Number(lien.getAttribute('href').slice('#zs-formule-'.length));
  const formules = document.querySelector('zs-formules');
  if (formules?.choisir(quantite)) ev.preventDefault();
});

/* ---------- Barre d'achat fixe ---------- */
class ZsBarreAchat extends HTMLElement {
  #observeur;

  connectedCallback() {
    this.addEventListener('click', this.#clic);
    document.addEventListener('zs:formule', this.#formule);
    quandPret(() => {
      const courant = document.querySelector('zs-formules')?.etat;
      if (courant) this.#ecrire(courant);

      const cible = this.#principal();
      if (cible && 'IntersectionObserver' in window && !this.#observeur) {
        this.#observeur = new IntersectionObserver((entrees) => {
          for (const e of entrees) this.#montrer(!e.isIntersecting && (petitEcran() || e.boundingClientRect.top < 0));
        });
        this.#observeur.observe(cible);
      }
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#clic);
    document.removeEventListener('zs:formule', this.#formule);
    this.#observeur?.disconnect();
    this.#observeur = undefined;
  }

  /** Le bouton d'achat principal du produit, celui du formulaire de la fiche */
  #principal() {
    return document.querySelector(
      `product-form-component[data-product-id="${this.dataset.productId}"] button[type="submit"][name="add"]`
    );
  }

  #clic = (ev) => {
    if (ev.target instanceof Element && ev.target.closest('[data-zs-ajouter]')) this.#principal()?.click();
  };

  #formule = (ev) => this.#ecrire(ev.detail);

  #ecrire({ court, total }) {
    const t = this.querySelector('[data-zs-resume]');
    if (t) t.textContent = `${court} · ${total}`;
  }

  /** Sur téléphone la barre est là dès l'arrivée, sur ordinateur une fois le bouton dépassé ; jamais quand le bouton est à l'écran */
  #montrer(oui) {
    this.classList.toggle('zs-barre--visible', oui);
    this.toggleAttribute('inert', !oui);
  }
}
if (!customElements.get('zs-barre-achat')) customElements.define('zs-barre-achat', ZsBarreAchat);

/* ---------- Vidéos de professionnels : lecteur YouTube sans cookie, chargé au clic, dans la page ---------- */
class ZsVideos extends HTMLElement {
  #declencheur = null;

  connectedCallback() {
    this.addEventListener('click', this.#clic);
    // l'événement close ne remonte pas : on l'écoute en capture pour vider le lecteur quel que soit le moyen de fermeture
    this.addEventListener('close', this.#vider, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#clic);
    this.removeEventListener('close', this.#vider, true);
  }

  #clic = (ev) => {
    const cible = ev.target instanceof Element ? ev.target : null;
    const dialogue = this.querySelector('dialog');
    const cadre = this.querySelector('[data-zs-cadre]');
    const carte = cible?.closest('[data-zs-video]');
    if (carte && dialogue && cadre) {
      if (carte.dataset.zsMp4) {
        // fichier de la boutique : lecteur du navigateur, lancé par ce clic (le son est permis)
        const video = document.createElement('video');
        video.src = carte.dataset.zsMp4;
        if (carte.dataset.zsAffiche) video.poster = carte.dataset.zsAffiche;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        video.preload = 'auto';
        video.setAttribute('aria-label', carte.dataset.titre || 'Vidéo');
        cadre.replaceChildren(video);
      } else {
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(carte.dataset.zsVideo)}?autoplay=1&rel=0&playsinline=1`;
        iframe.title = carte.dataset.titre || 'Vidéo';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        cadre.replaceChildren(iframe);
      }
      this.#declencheur = carte;
      dialogue.showModal();
      return;
    }
    // clic sur le fond (le dialogue lui-même) ou sur le bouton de fermeture
    if (dialogue && (cible === dialogue || cible?.closest('[data-zs-fermer]'))) {
      dialogue.close();
      this.#vider();
    }
  };

  /** Arrête la lecture (l'iframe ou la vidéo disparaît) et rend le focus à la vignette qui avait ouvert le lecteur */
  #vider = () => {
    const lecteur = this.querySelector('[data-zs-cadre] video');
    if (lecteur) {
      lecteur.pause();
      lecteur.removeAttribute('src');
      lecteur.load();
    }
    this.querySelector('[data-zs-cadre]')?.replaceChildren();
    this.#declencheur?.focus({ preventScroll: true });
    this.#declencheur = null;
  };
}
if (!customElements.get('zs-videos')) customElements.define('zs-videos', ZsVideos);

/* ---------- Bouton « Retour » des fiches : revient à la page d'où l'on vient, sinon suit son lien (collection du produit ou Boutique) ---------- */
document.addEventListener('click', (evenement) => {
  const lien = evenement.target instanceof Element ? evenement.target.closest('[data-zs-retour]') : null;
  if (!lien || evenement.defaultPrevented || evenement.button !== 0 || evenement.metaKey || evenement.ctrlKey || evenement.shiftKey || evenement.altKey) return;
  let memeSite = false;
  try {
    memeSite = Boolean(document.referrer) && new URL(document.referrer).origin === window.location.origin;
  } catch {
    // adresse d'origine illisible : on suit le lien
  }
  if (memeSite && window.history.length > 1) {
    evenement.preventDefault();
    window.history.back();
  }
});
